// Un post del calendario: qué lleva, qué formato es y qué le falta —
// ADR-2026-09-15-un-lanzamiento-de-redes-es-un-calendario-de-posts.
//
// Los topes son de la API de Instagram (Content Publishing), no gustos nuestros.
// Cada uno con su motivo al lado: si alguno cambia es porque cambió la
// documentación de Meta, igual que en `canales.ts`.
//
// Sin React y sin red: el editor lo usa para avisar mientras se escribe, y el
// guardado para frenar. Los dos tienen que decir lo mismo.

/**
 * Qué es el post, DEDUCIDO de lo que lleva adentro.
 *
 * A propósito no es un campo que el usuario elija: un selector de formato al
 * lado de la lista de medios es un dato que se desincroniza solo —se saca el
 * segundo video y el post sigue diciendo "carrusel"—. Las piezas mandan.
 */
export type FormatoDePost = "post" | "carrusel" | "reel";

export interface MedioDePost {
  /** El id del VÍNCULO (`lanzamientos_imagenes`), no el de la creación. */
  id: string;
  /**
   * El id de la creación (`imagenes_generadas`) cuando la pieza salió de una, y
   * null cuando la subió el usuario.
   *
   * Existe porque el selector de creaciones tacha lo ya usado comparando contra
   * ESTE id: pasarle el del vínculo no matcheaba nunca y volvía a ofrecer las
   * piezas que el post ya tenía (ADR-2026-09-16-lo-ya-usado-se-mira-por-la-creacion).
   */
  generadaId: string | null;
  fileName: string;
  esVideo: boolean;
  orden: number;
}

export type EstadoDePost = "pendiente" | "publicando" | "publicado" | "error";

export interface PostDeRedes {
  id: string;
  /** Cuándo sale. Null mientras se está armando. */
  cuando: Date | null;
  /** El copy. Es la columna `descripcion` de la fila. */
  texto: string;
  medios: MedioDePost[];
  estado: EstadoDePost;
  error: string | null;
}

// ── Los topes de Instagram ──────────────────────────────────────────────────

/** "carousels can combine up to 10 images/videos". */
export const MAX_PIEZAS = 10;

/** Largo máximo del pie de un post en Instagram. */
export const MAX_TEXTO = 2200;

/** Instagram corta después de 30 hashtags: los de más no los cuenta. */
export const MAX_HASHTAGS = 30;

/**
 * "limited to 100 API-published posts within a 24-hour moving period".
 *
 * Un carrusel cuenta como UNO. Se valida aunque parezca inalcanzable porque el
 * día que alguien cargue un lanzamiento grande de golpe, el error de Meta llega
 * cuando ya salieron las primeras cien y nadie sabe cuáles faltaron.
 */
export const MAX_POR_DIA = 100;

/** Reconoce `#palabra` con acentos y números. No cuenta el `#` suelto. */
const HASHTAG = /#[\p{L}\p{N}_]+/gu;

export function contarHashtags(texto: string): number {
  return (texto.match(HASHTAG) ?? []).length;
}

/**
 * Qué formato es, según las piezas.
 *
 * Un video solo es un reel porque hoy en Instagram un video del feed ES un
 * reel: no hay un "video de feed" separado que elegir.
 */
export function formatoDe(medios: readonly MedioDePost[]): FormatoDePost {
  if (medios.length > 1) return "carrusel";
  if (medios.length === 1 && medios[0]!.esVideo) return "reel";
  return "post";
}

export function nombreDelFormato(f: FormatoDePost): string {
  switch (f) {
    case "carrusel":
      return "Carrusel";
    case "reel":
      return "Reel";
    default:
      return "Publicación";
  }
}

/** En qué card del editor se arregla cada problema. */
export type SeccionDePost = "medios" | "texto" | "cuando";

export interface ProblemaDePost {
  seccion: SeccionDePost;
  texto: string;
}

/**
 * Qué le falta al post, y DÓNDE se arregla cada cosa.
 *
 * Saber la sección no es cosmético: el encabezado del editor cuenta cuántas
 * faltan y cada card avisa la suya, igual que la ficha de un producto
 * (ADR-2026-09-15-el-estado-del-post-va-en-el-encabezado). Sin el vínculo, un
 * "faltan 2 datos" arriba obliga a abrir las tres cards a ver cuáles.
 *
 * Devuelve TODO lo que está mal y no sólo lo primero: arreglar un problema para
 * descubrir el siguiente es la forma más lenta de completar un formulario.
 */
export function problemasConSeccion(
  p: PostDeRedes,
  ahora: Date = new Date(),
): ProblemaDePost[] {
  const malo: ProblemaDePost[] = [];

  if (p.medios.length === 0) {
    malo.push({ seccion: "medios", texto: "Falta la foto o el video" });
  }
  if (p.medios.length > MAX_PIEZAS) {
    malo.push({
      seccion: "medios",
      texto: `Un carrusel admite hasta ${MAX_PIEZAS} piezas y tiene ${p.medios.length}`,
    });
  }
  if (p.texto.length > MAX_TEXTO) {
    malo.push({
      seccion: "texto",
      texto: `El texto tiene ${p.texto.length} caracteres y el tope es ${MAX_TEXTO}`,
    });
  }
  const tags = contarHashtags(p.texto);
  if (tags > MAX_HASHTAGS) {
    malo.push({
      seccion: "texto",
      texto: `Instagram cuenta hasta ${MAX_HASHTAGS} hashtags y hay ${tags}`,
    });
  }
  if (!p.cuando) {
    malo.push({ seccion: "cuando", texto: "Falta la fecha y la hora" });
  } else if (p.estado === "pendiente" && p.cuando.getTime() < ahora.getTime()) {
    // No es un detalle: un post pendiente con fecha pasada no lo va a levantar
    // nadie nunca, y desde el calendario parece agendado.
    malo.push({ seccion: "cuando", texto: "La fecha ya pasó" });
  }

  return malo;
}

/** Lo mismo, en texto plano. Lo usa el calendario, al que no le importa dónde. */
export function problemasDelPost(p: PostDeRedes, ahora: Date = new Date()): string[] {
  return problemasConSeccion(p, ahora).map((x) => x.texto);
}

/** El primero de esa card, para el aviso en ámbar de su cabecera. */
export function avisoDeSeccion(
  problemas: readonly ProblemaDePost[],
  seccion: SeccionDePost,
): string | null {
  return problemas.find((x) => x.seccion === seccion)?.texto ?? null;
}

/**
 * Un post al que no se le puso NADA: ni una pieza ni una palabra.
 *
 * Tocar "+" en un día crea la fila al toque —hace falta para poder colgarle las
 * fotos—, así que asomarse y volver dejaba un post fantasma en el calendario.
 * Al cerrarlo, si sigue así, se descarta
 * (ADR-2026-09-15-un-post-al-que-no-se-le-puso-nada-se-descarta).
 *
 * Mira el texto ADEMÁS de las piezas: sin piezas un post no puede salir, pero
 * si alguien escribió el copy primero, borrárselo en silencio es perder trabajo
 * de verdad. Ése queda, avisando en ámbar que le falta la foto.
 */
export function estaVacio(p: PostDeRedes): boolean {
  return p.medios.length === 0 && p.texto.trim() === "";
}

export function estaListo(p: PostDeRedes, ahora: Date = new Date()): boolean {
  return problemasDelPost(p, ahora).length === 0;
}

/**
 * Los días en los que la agenda se pasa del tope diario de la API.
 *
 * Mira la ventana de 24 h como el día del calendario y no como una ventana
 * móvil: es más estricto y mucho más explicable —"el 3 de octubre tenés 101"—.
 * La ventana móvil real de Meta nunca va a rebotar algo que esto deje pasar.
 */
export function diasPasadosDeRosca(
  posts: ReadonlyArray<{ cuando: Date | null }>,
): Array<{ dia: string; cuantos: number }> {
  const porDia = new Map<string, number>();
  for (const p of posts) {
    if (!p.cuando) continue;
    const clave = `${p.cuando.getFullYear()}-${p.cuando.getMonth()}-${p.cuando.getDate()}`;
    porDia.set(clave, (porDia.get(clave) ?? 0) + 1);
  }
  return [...porDia.entries()]
    .filter(([, cuantos]) => cuantos > MAX_POR_DIA)
    .map(([dia, cuantos]) => ({ dia, cuantos }));
}
